import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { BookOpen, Lock, Check, X, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_BADGE = {
  A1: 'bg-green-100 text-green-800 border-green-300',
  A2: 'bg-teal-100 text-teal-800 border-teal-300',
  B1: 'bg-blue-100 text-blue-800 border-blue-300',
  B2: 'bg-blue-100 text-blue-800 border-blue-300',
  C1: 'bg-sky-100 text-sky-800 border-sky-300',
  C2: 'bg-cyan-100 text-cyan-800 border-cyan-300',
};

const GrammarTopicPage = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topic, setTopic] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentExercise, setCurrentExercise] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(0);

  const fetchTopic = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/grammar/topics/${topicId}`);
      if (response.status === 403) {
        toast.error('This is a premium topic. Please upgrade to access.');
        navigate('/subscription');
        return;
      }
      if (response.ok) setTopic(await response.json());
    } catch (error) {
      console.error('Failed to fetch topic:', error);
      toast.error('Failed to load grammar topic');
    } finally {
      setLoading(false);
    }
  }, [topicId, navigate]);

  const fetchExercises = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/grammar/exercises/${topicId}`);
      if (response.ok) setExercises(await response.json());
    } catch (error) {
      console.error('Failed to fetch exercises:', error);
    }
  }, [topicId]);

  useEffect(() => {
    fetchTopic();
    fetchExercises();
  }, [fetchTopic, fetchExercises]);

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/grammar/exercises/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exercise_id: exercises[currentExercise].exercise_id,
          user_answer: userAnswer,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setFeedback(data);
        setCompleted(c => c + 1);
        if (data.correct) setScore(s => s + 1);
      }
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  const nextExercise = () => {
    if (currentExercise < exercises.length - 1) {
      setCurrentExercise(c => c + 1);
      setUserAnswer('');
      setFeedback(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-babyblue-50 to-white">
        <div className="spinner"></div>
      </div>
    );
  }
  if (!topic) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <h1 className="font-heading text-3xl font-bold text-babyblue-900 mb-3">
              Topic could not be loaded
            </h1>
            <p className="text-babyblue-600 mb-6">
              Something went wrong loading this grammar topic. Please try again.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setLoading(true); fetchTopic(); fetchExercises(); }}
                className="btn-primary"
                data-testid="retry-topic"
              >
                Retry
              </button>
              <Link to="/grammar" className="btn-secondary">
                Back to Grammar
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="grammar-topic-page">
      <Navbar />

      <main className="flex-1 py-10 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8">
            <Link to="/grammar" className="text-primary hover:text-babyblue-800 mb-4 inline-flex items-center gap-1 text-sm font-medium transition-colors" data-testid="back-to-grammar">
              <ArrowLeft className="w-4 h-4" />
              Back to Grammar
            </Link>
            <div className="flex items-center gap-2 mb-4 mt-2">
              <span className={`text-xs font-bold px-3 py-1 rounded-full border ${LEVEL_BADGE[topic.level]}`}>
                {topic.level}
              </span>
              {topic.premium && (
                <span className="bg-amber-100 text-amber-800 border border-amber-300 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Premium
                </span>
              )}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-babyblue-800" data-testid="topic-title">
              {topic.title}
            </h1>
          </div>

          {/* Lesson content */}
          <div className="bg-white rounded-2xl shadow-sm border border-babyblue-200 p-6 md:p-8 mb-8">
            <div
              className="grammar-content prose prose-blue max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(topic.explanation || '') }}
            />
          </div>

          {topic.examples && topic.examples.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-babyblue-200 p-6 md:p-8 mb-8">
              <h3 className="font-heading text-xl font-bold text-babyblue-800 mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-primary" />
                Examples
              </h3>
              <ul className="space-y-2">
                {topic.examples.map((example, index) => (
                  <li key={index} className="text-babyblue-800 leading-relaxed pl-4 border-l-2 border-babyblue-200">
                    {example}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {exercises.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-babyblue-200 p-6 md:p-8" data-testid="exercises-section">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading text-xl font-bold text-babyblue-800">Practice Exercises</h3>
                  <span className="text-sm font-semibold text-babyblue-600">
                    {currentExercise + 1} / {exercises.length}
                  </span>
                </div>
                <div className="h-2 bg-babyblue-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${(completed / exercises.length) * 100}%` }}
                  />
                </div>
                <p className="text-sm text-babyblue-600 mt-2">Score: {score} / {completed}</p>
              </div>

              <div className="mb-6">
                <p className="text-lg text-babyblue-800 mb-4 font-medium">{exercises[currentExercise].question}</p>
                <input
                  type="text"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !feedback && handleSubmit()}
                  className="w-full px-4 py-3 bg-white border-2 border-babyblue-200 rounded-lg text-babyblue-900 placeholder-babyblue-400 focus:border-primary focus:outline-none transition-colors"
                  placeholder="Type your answer…"
                  disabled={!!feedback}
                  data-testid="exercise-input"
                />
              </div>

              {feedback && (
                <div className={`p-4 rounded-xl mb-6 border-2 ${feedback.correct ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`} data-testid="exercise-feedback">
                  <div className="flex items-center gap-2 mb-2">
                    {feedback.correct ? (
                      <>
                        <Check className="w-5 h-5 text-green-700" />
                        <span className="font-bold text-green-800">Correct!</span>
                      </>
                    ) : (
                      <>
                        <X className="w-5 h-5 text-red-700" />
                        <span className="font-bold text-red-800">Incorrect</span>
                      </>
                    )}
                  </div>
                  {!feedback.correct && (
                    <p className="text-babyblue-800 mb-2">
                      <strong>Correct answer:</strong> {feedback.correct_answer}
                    </p>
                  )}
                  {feedback.explanation && <p className="text-babyblue-700">{feedback.explanation}</p>}
                </div>
              )}

              <div className="flex gap-4">
                {!feedback ? (
                  <button
                    onClick={handleSubmit}
                    className="btn-primary"
                    disabled={!userAnswer.trim()}
                    data-testid="submit-answer-button"
                  >
                    Submit Answer
                  </button>
                ) : (
                  <button
                    onClick={nextExercise}
                    className="btn-primary"
                    disabled={currentExercise >= exercises.length - 1}
                    data-testid="next-exercise-button"
                  >
                    {currentExercise < exercises.length - 1 ? 'Next Exercise' : 'Complete'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GrammarTopicPage;
